"use client";

import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowRight, Lock, Mail } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { icons } from "../../assets";
import AppLoader from "../../components/appLoader";
import AuthLayout from "../../components/authLayout";
import CustomButton from "../../components/customButton";
import TextInput from "../../components/textInput";
import useUserStore from "../../zustand/useUserStore";
import { adminLogin } from "../../services/Auth";
import { openSnackAlert } from "../../helper";

export default function LoginPage() {
  const { setUserData } = useUserStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();

  const validate = () => {
    const newErrors = {};

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      console.log("Validation failed");
      openSnackAlert(errors?.email || errors?.password, "error");
      return;
    }
    setIsLoading(true);
    const payload = { email, password };

    try {
      const response = await adminLogin(payload);
      const resData = response?.data;

      if (resData?.status === "success") {
        setUserData(resData?.data.user);
        localStorage.setItem("token", resData?.data.token);
        navigate("/");
      } else {
        openSnackAlert(resData?.message || "Login failed", "error");
      }
    } catch (error) {
      console.error(error);
      openSnackAlert(error?.message || "Something went wrong", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Box
        p={6}
        py={4}
        mt={3}
        width={{ xs: 400, sm: 520 }}
        bgcolor="#fff"
        borderRadius="16px"
        boxShadow="0 4px 30px rgba(0, 0, 0, 0.1)"
      >
        <Box textAlign="center" mb={4}>
          <Box>
            <img
              src={icons.applogo}
              alt="logo"
              style={{ width: "120px", height: "70px" }}
            />
          </Box>
          <Typography
            variant="h4"
            fontWeight={700}
            color="primary"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
            }}
          >
            Welcome Back
          </Typography>
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            Sign in to your dashboard
          </Typography>
        </Box>

        <Box component="form">
          <TextInput
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            error={!!errors.email}
            helperText={errors.email}
            variant="softInput"
            InputStartIcon={
              <Mail size={20} color={theme.palette.text.secondary} />
            }
            sx={{ mb: 2 }}
          />
          <TextInput
            fullWidth
            label="Password"
            type={"password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            error={!!errors.password}
            helperText={errors.password}
            variant="softInput"
            InputStartIcon={
              <Lock size={20} color={theme.palette.text.secondary} />
            }
            showPassIcon={true}
            sx={{ mb: 3 }}
          />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            my={1}
          >
            <FormControlLabel
              control={<Checkbox sx={{ color: "#fff" }} size="small" />}
              label={
                <Typography color="#000" fontSize={12}>
                  Remember Me
                </Typography>
              }
            />
            <Typography
              variant="label"
              color={"primary.main"}
              sx={{ cursor: "pointer" }}
              onClick={() => navigate("/forgot-password")}
            >
              Forgot Password?
            </Typography>
          </Box>

          <Box display={"flex"} justifyContent={"center"}>
            <CustomButton
              handlePressBtn={handleSubmit}
              btnLabel="Sign In"
              type="submit"
              fullWidth
              variant="webbutton"
              endIcon={<ArrowRight size={20} />}
              sx={{
                fontSize: 16,
                fontWeight: 600,
                py: 1.5,
                width: "100%",
              }}
            />
          </Box>
        </Box>
      </Box>
      <AppLoader isVisible={isLoading} />
    </AuthLayout>
  );
}
